import { keyframes, styled } from "goober";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type CardRect,
  cornerRadius,
  loopbackMargin,
  routeConnectors,
} from "../../lib/routeConnectors";
import { useTenantCustomDomain } from "../../hooks/useTenantCustomDomain";
import { playPop } from "../../lib/sounds";
import {
  getStepCopyableEmailAddress,
  getStepCopyableUrl,
} from "../../lib/stepUrl";
import { recallStepFile } from "../../lib/lastOpenStepFile";
import { publicDomain, publicUrl } from "../../lib/tenantOrigin";
import { makeUrl, navigate, routes } from "../../lib/urls";
import type { StepData, WorkflowData } from "../Workflow";
import { StepContextMenu } from "./StepContextMenu";

// LinkData for StepMap uses resolved version IDs (transformed from canonical IDs)
type LinkData = {
  id: string;
  fromStepId: string;
  toStepId: string;
  createdAt: number;
};

import { GridStep } from "./GridStep";
import { useStepMapKeyboardNav } from "./useStepMapKeyboardNav";

export const rowGap = 4;
export const gridPadding = 12;
export const gridStepHeight = 48;

type StepMapLayout = Map<string, { row: number; col: number }>;

type LinkLayoutData = {
  fromStepId: string;
  toStepId: string;
};

export function getStepMapPadding(
  layout: StepMapLayout,
  links: readonly LinkLayoutData[]
) {
  const cells = Array.from(layout.values());
  const colCount = Math.max(...cells.map((p) => p.col), -1) + 1;

  let loopAbove = false;
  let loopBelow = false;
  let loopRight = false;
  let loopLeft = false;

  for (const link of links) {
    const src = layout.get(link.fromStepId);
    const tgt = layout.get(link.toStepId);
    if (!src || !tgt) continue;

    if (tgt.col <= src.col) {
      if (src.row <= tgt.row) loopAbove = true;
      else loopBelow = true;
      if (src.col === colCount - 1) loopRight = true;
      if (tgt.col === 0) loopLeft = true;
    }
  }

  const loopExtra = loopbackMargin + cornerRadius;

  return {
    padTop: loopAbove ? gridPadding + loopExtra : gridPadding,
    padBottom: loopBelow ? gridPadding + loopExtra : gridPadding,
    padRight: loopRight ? gridPadding + loopExtra : gridPadding,
    padLeft: loopLeft ? gridPadding + loopExtra : gridPadding,
  };
}

const Grid = styled("div")`
  display: grid;
  row-gap: ${rowGap}px;
  column-gap: 28px;
  position: relative;
  overflow: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  height: 100%;
  cursor: grab;
  mask-image: linear-gradient(
    to bottom,
    transparent,
    black var(--fade-top, 0px)
  );
  &.panning {
    cursor: grabbing;
    user-select: none;
  }
`;

const dashFlow = keyframes`
  to {
    stroke-dashoffset: -3;
  }
`;

const Lines = styled("svg")`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: visible;

  .active {
    stroke-dasharray: 1 2;
    animation: ${dashFlow} 700ms linear infinite;
  }

  .dimmed {
    opacity: 0.6;
    filter: blur(3px);
    transition:
      opacity 200ms ease,
      filter 200ms ease;
  }
`;

type ContextMenuState = {
  step: StepData;
  x: number;
  y: number;
} | null;

type Props = {
  layout: StepMapLayout;
  steps: StepData[];
  links: LinkData[];
  workflow: WorkflowData;
  spaceSlug: string;
  selectedStep: string | null;
  branchId: string;
  commitId: string;
  isOnMainBranch: boolean;
  isReadOnly?: boolean;
  canWrite: boolean;
  aiPresentOnStepName?: string;
  presentByStep: Map<string, Set<string>>;
  enableSounds: boolean;
  changedCanonicalIds: Set<string>;
  highlightedCanonicalIds?: Set<string> | null;
  hoveredCanonicalId?: string | null;
  onStepHover?: (canonicalId: string | null) => void;
};

const emptySet = new Set<string>();

export function StepMap({
  layout,
  steps,
  links,
  workflow,
  spaceSlug,
  selectedStep,
  branchId,
  commitId,
  isOnMainBranch,
  isReadOnly = false,
  canWrite,
  aiPresentOnStepName,
  presentByStep,
  enableSounds,
  changedCanonicalIds,
  highlightedCanonicalIds,
  hoveredCanonicalId,
  onStepHover,
}: Props) {
  const customDomain = useTenantCustomDomain();
  const externalPublicUrl = publicUrl();
  const domain = publicDomain();
  // Resolve stable identifier to physical ID for layout/ref operations
  const selectedStepId =
    steps.find((s) => s.canonicalId === selectedStep)?.id ?? null;
  const hoveredStepId =
    steps.find((s) => s.canonicalId === hoveredCanonicalId)?.id ?? null;
  const [hoverDirection, setHoverDirection] = useState<"up" | "down" | "both">(
    "both"
  );
  const [hoveredLinkId, setHoveredLinkId] = useState<string | null>(null);

  // The focused step and its up/downstream flow. Focus is the hovered
  // connector's two endpoints, else the hovered step (whose edge picks the
  // direction — left edge = upstream, right edge = downstream, middle = both),
  // else the selected step (always both). An edge or connector hover dims
  // everything unrelated; a middle hover or bare selection just lights the
  // flow. Physical step ids equal canonical ids here (both are the step
  // directory).
  const focusGraph = useMemo(() => {
    if (hoveredLinkId) {
      const link = links.find((l) => l.id === hoveredLinkId);
      if (!link) return null;
      const canonicalById = new Map(steps.map((s) => [s.id, s.canonicalId]));
      const relatedStepCids = new Set(
        [link.fromStepId, link.toStepId].flatMap((id) => {
          const c = canonicalById.get(id);
          return c ? [c] : [];
        })
      );
      return {
        relatedLinkIds: new Set([hoveredLinkId]),
        relatedStepCids,
        dimUnrelated: true,
      };
    }
    const focusStepId = hoveredStepId ?? selectedStepId;
    if (!focusStepId) return null;
    const direction = hoveredStepId ? hoverDirection : "both";

    const relatedStepIds = new Set<string>([focusStepId]);
    const relatedLinkIds = new Set<string>();
    const walk = (dir: "up" | "down") => {
      const visited = new Set<string>();
      const queue = [focusStepId];
      while (queue.length > 0) {
        const stepId = queue.pop()!;
        if (visited.has(stepId)) continue;
        visited.add(stepId);
        relatedStepIds.add(stepId);
        for (const l of links) {
          if (dir === "up" && l.toStepId === stepId) {
            relatedLinkIds.add(l.id);
            queue.push(l.fromStepId);
          } else if (dir === "down" && l.fromStepId === stepId) {
            relatedLinkIds.add(l.id);
            queue.push(l.toStepId);
          }
        }
      }
    };
    if (direction !== "down") walk("up");
    if (direction !== "up") walk("down");

    const canonicalById = new Map(steps.map((s) => [s.id, s.canonicalId]));
    const relatedStepCids = new Set(
      [...relatedStepIds].flatMap((id) => {
        const c = canonicalById.get(id);
        return c ? [c] : [];
      })
    );
    return {
      relatedLinkIds,
      relatedStepCids,
      dimUnrelated: direction !== "both",
    };
  }, [
    hoveredLinkId,
    hoveredStepId,
    hoverDirection,
    selectedStepId,
    links,
    steps,
  ]);

  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const [stepRefs] = useState(new Map<string, HTMLAnchorElement>());
  const [gridRef, setGridRef] = useState<HTMLDivElement | null>(null);
  const linesRef = useRef<SVGSVGElement>(null);
  // Persistent path elements keyed by link id, so the .active animation
  // keeps running across measurements instead of restarting each time.
  const pathRefsRef = useRef(
    new Map<
      string,
      {
        bg: SVGPathElement;
        hit: SVGPathElement;
        fg: { casing: SVGPathElement; dash: SVGPathElement } | null;
      }
    >()
  );

  // Track new cards for entrance animation — use canonicalId (stable across edits)
  const seenCanonicalIdsRef = useRef<Set<string>>(new Set());
  const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set());
  const hasRevealedRef = useRef(false);

  // Canvas-style drag-to-pan
  const panRef = useRef<{
    x: number;
    y: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);

  useEffect(() => {
    if (!gridRef) return;

    const onMouseDown = (e: MouseEvent) => {
      // Middle-click anywhere, or left-click on grid/SVG background
      const isBackground =
        e.target === gridRef || !!linesRef.current?.contains(e.target as Node);
      if (e.button === 1 || (e.button === 0 && isBackground)) {
        e.preventDefault();
        panRef.current = {
          x: e.clientX,
          y: e.clientY,
          scrollLeft: gridRef.scrollLeft,
          scrollTop: gridRef.scrollTop,
        };
        gridRef.classList.add("panning");
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!panRef.current) return;
      gridRef.scrollLeft =
        panRef.current.scrollLeft - (e.clientX - panRef.current.x);
      gridRef.scrollTop =
        panRef.current.scrollTop - (e.clientY - panRef.current.y);
    };

    const onMouseUp = (e: MouseEvent) => {
      const pan = panRef.current;
      if (pan) {
        panRef.current = null;
        gridRef.classList.remove("panning");
        const moved = Math.hypot(e.clientX - pan.x, e.clientY - pan.y);
        if (selectedStep !== null && e.button === 0 && moved < 4) {
          navigate(routes.space.workflow, {
            spaceId: workflow.spaceId,
            workflowId: workflow.id,
            step: "",
            file: "",
          });
        }
      }
    };

    gridRef.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      gridRef.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [gridRef, selectedStep, workflow.id, workflow.spaceId]);

  useStepMapKeyboardNav({
    layout,
    steps,
    selectedStep,
    spaceId: workflow.spaceId,
    workflowId: workflow.id,
  });

  useEffect(() => {
    if (!selectedStepId) return;
    const el = stepRefs.get(selectedStepId);
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [selectedStepId, stepRefs]);

  // Detect genuinely new steps (first creation only, not edits/executions).
  // Uses canonicalId (stable across versions) and a persistent "seen" set
  // so each step only triggers the pop once, ever.
  useEffect(() => {
    if (!hasRevealedRef.current) {
      // Still loading — seed the seen set, don't trigger anything
      for (const s of steps) seenCanonicalIdsRef.current.add(s.canonicalId);
      return;
    }

    const newStepIds: string[] = [];
    for (const s of steps) {
      if (!seenCanonicalIdsRef.current.has(s.canonicalId)) {
        seenCanonicalIdsRef.current.add(s.canonicalId);
        newStepIds.push(s.id);
      }
    }

    if (newStepIds.length > 0) {
      const ids = new Set(newStepIds);
      setEnteringIds(ids);
      playPop();
      const timer = setTimeout(() => setEnteringIds(new Set()), 300);
      return () => clearTimeout(timer);
    }
  }, [steps]);

  const getMeasurements = useCallback(() => {
    if (stepRefs.size === 0 || !gridRef) return;

    const containerRect = gridRef.getBoundingClientRect();
    // The SVG is positioned at the content origin (top: 0, left: 0 of the
    // scrollable area), so we must offset viewport-relative measurements by
    // the current scroll position to stay aligned with the content.
    const sx = gridRef.scrollLeft;
    const sy = gridRef.scrollTop;
    const cardRects = new Map<string, CardRect>();

    for (const [stepId, el] of stepRefs) {
      const pos = layout.get(stepId);
      if (!pos) continue;
      const rect = el.getBoundingClientRect();
      cardRects.set(stepId, {
        id: stepId,
        col: pos.col,
        row: pos.row,
        left: rect.left - containerRect.left + sx,
        right: rect.right - containerRect.left + sx,
        top: rect.top - containerRect.top + sy,
        bottom: rect.bottom - containerRect.top + sy,
        centerY: rect.top + rect.height / 2 - containerRect.top + sy,
      });
    }

    // Write connector paths directly to the SVG DOM so they stay in sync
    // with card positions during continuous resize (no React render delay).
    const svg = linesRef.current;
    if (svg) {
      const paths = routeConnectors(cardRects, links);

      // Links on the focused step's flow animate; when the focus comes from a
      // hover, everything unrelated dims.
      const relatedLinkIds = focusGraph?.relatedLinkIds ?? emptySet;
      const dimUnrelated = focusGraph?.dimUnrelated ?? false;

      const ns = "http://www.w3.org/2000/svg";
      const pathRefs = pathRefsRef.current;

      const createPath = (stroke: string, cls?: string, width = 1) => {
        const el = document.createElementNS(ns, "path");
        el.setAttribute("stroke", stroke);
        el.setAttribute("stroke-width", String(width));
        el.setAttribute("fill", "none");
        el.setAttribute("stroke-linejoin", "round");
        el.setAttribute("stroke-linecap", "round");
        if (cls) el.classList.add(cls);
        return el;
      };

      // Build a physical-ID → canonical-ID map so we can check which links
      // connect steps that are part of the highlighted run.
      const idToCanonical = new Map<string, string>();
      if (highlightedCanonicalIds) {
        for (const s of steps) idToCanonical.set(s.id, s.canonicalId);
      }
      const isLinkDimmed = (p: {
        id: string;
        fromStepId: string;
        toStepId: string;
      }) => {
        if (dimUnrelated) return !relatedLinkIds.has(p.id);
        if (!highlightedCanonicalIds) return false;
        const fromCid = idToCanonical.get(p.fromStepId);
        const toCid = idToCanonical.get(p.toStepId);
        return (
          !fromCid ||
          !toCid ||
          !highlightedCanonicalIds.has(fromCid) ||
          !highlightedCanonicalIds.has(toCid)
        );
      };

      // Drop any link elements that no longer exist.
      const liveIds = new Set(paths.map((p) => p.id));
      for (const [id, refs] of pathRefs) {
        if (!liveIds.has(id)) {
          refs.bg.remove();
          refs.hit.remove();
          refs.fg?.casing.remove();
          refs.fg?.dash.remove();
          pathRefs.delete(id);
        }
      }

      // Reconcile bgs first. New bgs are inserted at the front so they sit
      // beneath any existing fgs (later siblings paint on top in SVG).
      for (const p of paths) {
        let refs = pathRefs.get(p.id);
        if (!refs) {
          const bg = createPath("var(--gray-400)");
          svg.insertBefore(bg, svg.firstChild);
          const hit = createPath("transparent", undefined, 14);
          hit.style.pointerEvents = "stroke";
          const linkId = p.id;
          hit.addEventListener("mouseenter", () => setHoveredLinkId(linkId));
          hit.addEventListener("mouseleave", () =>
            setHoveredLinkId((cur) => (cur === linkId ? null : cur))
          );
          svg.appendChild(hit);
          refs = { bg, hit, fg: null };
          pathRefs.set(p.id, refs);
        }
        refs.bg.setAttribute("d", p.d);
        refs.bg.classList.toggle("dimmed", isLinkDimmed(p));
        refs.hit.setAttribute("d", p.d);
      }

      // Reconcile fgs (active animated dashes) on top. Each dash gets a
      // surface-colored casing appended directly beneath it — not all casings
      // then all dashes — so a dash's gaps reveal its own casing (canvas
      // color) rather than a crossing line, keeping overlapping flow lines
      // legible. Reusing existing elements keeps the dashFlow animation from
      // restarting.
      for (const p of paths) {
        const refs = pathRefs.get(p.id)!;
        if (relatedLinkIds.has(p.id)) {
          if (!refs.fg) {
            const casing = createPath("var(--surface-primary)", undefined, 3);
            const dash = createPath("var(--gray-1000)", "active");
            svg.appendChild(casing);
            svg.appendChild(dash);
            refs.fg = { casing, dash };
          }
          refs.fg.casing.setAttribute("d", p.d);
          refs.fg.dash.setAttribute("d", p.d);
        } else if (refs.fg) {
          refs.fg.casing.remove();
          refs.fg.dash.remove();
          refs.fg = null;
        }
      }
    }
  }, [
    links,
    stepRefs,
    gridRef,
    layout,
    focusGraph,
    highlightedCanonicalIds,
    steps,
  ]);

  useEffect(() => {
    if (!gridRef) return;
    const resizeObserver = new ResizeObserver(getMeasurements);
    resizeObserver.observe(gridRef);
    gridRef.addEventListener("scroll", getMeasurements);
    return () => {
      resizeObserver.disconnect();
      gridRef.removeEventListener("scroll", getMeasurements);
    };
  }, [gridRef, getMeasurements]);

  useEffect(() => {
    if (!gridRef) return;
    const updateFade = () => {
      gridRef.style.setProperty(
        "--fade-top",
        `${Math.min(gridRef.scrollTop, 16)}px`
      );
    };
    updateFade();
    gridRef.addEventListener("scroll", updateFade, { passive: true });
    return () => gridRef.removeEventListener("scroll", updateFade);
  }, [gridRef]);

  // Mark as revealed on first mount so new-step detection starts working
  useEffect(() => {
    if (!hasRevealedRef.current) {
      hasRevealedRef.current = true;
      getMeasurements();
    }
  }, [getMeasurements]);

  const incomingLinksMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const link of links) {
      const existing = map.get(link.toStepId);
      if (existing) {
        existing.push(link.fromStepId);
      } else {
        map.set(link.toStepId, [link.fromStepId]);
      }
    }
    return map;
  }, [links]);

  const stepColorMap = useMemo(
    () => new Map(steps.map((s) => [s.id, s.color])),
    [steps]
  );

  const stepNameMap = useMemo(
    () => new Map(steps.map((s) => [s.id, s.name])),
    [steps]
  );

  const cells = Array.from(layout.values());
  const rowCount = Math.max(...cells.map((p) => p.row), -1) + 1;
  const colCount = Math.max(...cells.map((p) => p.col), -1) + 1;
  const { padTop, padBottom, padRight, padLeft } = useMemo(
    () => getStepMapPadding(layout, links),
    [layout, links]
  );

  return (
    <Grid
      ref={setGridRef}
      style={{
        gridTemplateColumns: `repeat(${colCount}, minmax(120px, 1fr))`,
        gridTemplateRows: `repeat(${rowCount}, ${gridStepHeight}px)`,
        padding: `${padTop}px ${padRight}px ${padBottom}px ${padLeft}px`,
      }}
    >
      {steps.map((step) => {
        const pos = layout.get(step.id);
        if (!pos) return null;

        const isSelected = selectedStep === step.canonicalId;
        const to = makeUrl(routes.space.workflow, {
          spaceId: workflow.spaceId,
          workflowId: workflow.id,
          step: isSelected ? "" : step.canonicalId,
          file: isSelected
            ? ""
            : (recallStepFile(workflow.id, step.canonicalId) ?? ""),
        });

        const isEntering = enteringIds.has(step.id);
        const entranceDelay = isEntering
          ? pos.col * 50 + pos.row * 30
          : undefined;

        return (
          <GridStep
            ref={(el) => {
              if (el) {
                stepRefs.set(step.id, el);
              }
            }}
            height={gridStepHeight}
            pos={pos}
            to={to}
            key={step.canonicalId}
            step={step}
            isSelected={isSelected}
            incomingFromStepIds={incomingLinksMap.get(step.id) ?? []}
            stepColorMap={stepColorMap}
            stepNameMap={stepNameMap}
            onContextMenu={(e, s) => {
              if (isReadOnly) return;
              setContextMenu({ step: s, x: e.clientX, y: e.clientY });
            }}
            isEntering={isEntering}
            entranceDelay={entranceDelay}
            isChanged={changedCanonicalIds.has(step.canonicalId)}
            isDimmed={
              focusGraph?.dimUnrelated
                ? !focusGraph.relatedStepCids.has(step.canonicalId)
                : highlightedCanonicalIds != null &&
                  !highlightedCanonicalIds.has(step.canonicalId)
            }
            isAiPresent={step.name === aiPresentOnStepName}
            onHighlightHover={(hovered, direction) => {
              onStepHover?.(hovered ? step.canonicalId : null);
              setHoverDirection(hovered ? direction : "both");
            }}
            presentUserIds={presentByStep.get(step.id) ?? emptySet}
            enableSounds={enableSounds}
            copyableUrl={getStepCopyableUrl(
              step,
              branchId,
              isOnMainBranch,
              spaceSlug,
              externalPublicUrl,
              domain,
              customDomain
            )}
            copyableEmail={getStepCopyableEmailAddress(
              step,
              branchId,
              isOnMainBranch,
              spaceSlug,
              domain
            )}
          />
        );
      })}

      <Lines ref={linesRef} />

      {contextMenu && (
        <StepContextMenu
          step={contextMenu.step}
          x={contextMenu.x}
          y={contextMenu.y}
          workflowId={workflow.id}
          branchId={branchId}
          commitId={commitId}
          canWrite={canWrite}
          copyableUrl={getStepCopyableUrl(
            contextMenu.step,
            branchId,
            isOnMainBranch,
            spaceSlug,
            externalPublicUrl,
            domain,
            customDomain
          )}
          copyableEmail={getStepCopyableEmailAddress(
            contextMenu.step,
            branchId,
            isOnMainBranch,
            spaceSlug,
            domain
          )}
          onClose={() => setContextMenu(null)}
        />
      )}
    </Grid>
  );
}
